import React, { useRef } from "react";
import { useResponsive } from "../../hooks/useResponsive";
import { useNavigate, useLocation, Outlet } from "react-router-dom";

import { PDFDownloadLink } from '@react-pdf/renderer';
import PersonalityTestPDF from '../common/PersonalityTestPDF';
import CustomText from '../atoms/CustomText';
import { View } from "react-native-web";
import SvgDownload from '../icons/SvgDownload';
import GeneralDialog from '../common/GeneralDialog';

import { colors, } from '../../utils/styling';
import { getResultsDataFromStateOrStorage, ResultsData } from "../../utils/resultsData";

// ResultsData type imported from utils/resultsData

export default function QuizHeader() {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  // const [showInformational, setShowInformational] = React.useState(false);
  // const [buttonText, setButtonText] = React.useState("Why does this matter?");
  // const [buttonRoute, setButtonRoute] = React.useState("");
  const [showPDFLink, setShowPDFLink] = React.useState(false);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  

  const prevPage = location.state?.from;

  let resultsData = getResultsDataFromStateOrStorage(location.state);

  // Provide default values to prevent destructuring errors
  const defaultResultsData: ResultsData = {
    answers: [],
    specificAnswers: [],
    categoryScores: {},
    specificCategoryScores: {},
    dominantCategory: '',
    specificDominantCategory: '',
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || defaultResultsData;
  const { answers: mainAnswers, categoryScores: mainCategoryScores, dominantCategory: mainDominantCategory, sfAccount } = safeResultsData;

  // Use global transaction data from MainResults instead of making API call
  const [transactionData, setTransactionData] = React.useState<{
    status: string;
    transaction_id: string | null;
    error?: string;
  } | null>(null);
  
  React.useEffect(() => {
    // @ts-ignore
    if (window.transactionData) {
      // @ts-ignore
      setTransactionData(window.transactionData);
    }
    
    // Listen for changes to global transaction data
    const checkTransactionData = () => {
      // @ts-ignore
      if (window.transactionData && window.transactionData !== transactionData) {
        // @ts-ignore
        setTransactionData(window.transactionData);
      }
    };
    
    const interval = setInterval(checkTransactionData, 1000);
    return () => clearInterval(interval);
  }, [transactionData]);

  const hasPaid = (transactionData?.status === 'found' && transactionData.transaction_id) || localStorage.getItem('payment_success') !== null || localStorage.getItem('mpay_coupon_redeemed') !== null;
  const personalInfo = localStorage.getItem('mpay_personalInfo');
  const hasCompletedInfoCollection = personalInfo !== null;
  const showDownload = hasPaid && hasCompletedInfoCollection;
  
  // Safely parse personal info with error handling
  let parsedPersonalInfo = undefined;
  try {
    parsedPersonalInfo = personalInfo ? JSON.parse(personalInfo) : undefined;
  } catch (error) {
    console.error('Error parsing personal info from localStorage:', error);
    parsedPersonalInfo = undefined;
  }

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  return (
    <>
      {/* Navbar */}
      <div
        style={{
          width: "100%",
          height: "89",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "white",
          padding: "24px 32px 24px 32px",
          boxSizing: "border-box",
          position: "fixed",
          top: 0,
          left: 0,
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 3px",
          zIndex: 999,
        }}
      >
        {/* Gradient Title */}
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
          <img
            src={require('../img/money_personality_full_logo.png')}
            alt="FinAI Logo"
            style={{ height: isMobile ? 42 : 50, width: 'auto', objectFit: 'contain', display: 'block', cursor: 'pointer', marginRight: '20px' }}
            onClick={() => navigate('/')}
          />
        </View>
        {/* Right side: Buttons + X or Dropdown for mobile */}
        {isMobile ? (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowDropdown((prev) => !prev)}
              style={{
                background: "none",
                border: "none",
                fontSize: 32,
                cursor: "pointer",
                padding: 0,
                borderRadius: "100%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Menu"
            >
              {/* Hamburger icon */}
              <svg width="24" height="24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="7" x2="20" y2="7"/>
                <line x1="4" y1="12" x2="20" y2="12"/>
                <line x1="4" y1="17" x2="20" y2="17"/>
              </svg>
            </button>
            {/* Dropdown menu */}
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: 40,
                right: 0,
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                padding: showDropdown ? "16px 0" : "0 0",
                minWidth: 180,
                zIndex: 1000,
                opacity: showDropdown ? 1 : 0,
                transform: showDropdown ? "scale(1)" : "scale(0.95)",
                pointerEvents: showDropdown ? "auto" : "none",
                transition: "opacity 0.2s, transform 0.2s, padding 0.2s",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {/* {showInformational && (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    color: "#222",
                    fontSize: 18,
                    fontWeight: 500,
                    padding: "12px 24px",
                    textAlign: "left",
                    cursor: "pointer",
                    width: "100%",
                  }}
                  onClick={() => {
                    setShowDropdown(false);
                    // Check if we're on main results page and should scroll instead of navigate
                    if (location.pathname === "/quiz/results/main") {
                      // @ts-ignore
                      if (window.scrollToImportanceSection) {
                        // @ts-ignore
                        window.scrollToImportanceSection();
                      }
                    } else {
                      navigate(buttonRoute, { state: { ...safeResultsData, from: location.pathname }});
                    }
                  }}
                >
                  {buttonText}
                </button>
              )} */}
              {/* {showDownload && (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    color: "#222",
                    fontSize: 18,
                    fontWeight: 500,
                    padding: "12px 24px",
                    textAlign: "left",
                    cursor: "pointer",
                    width: "100%",
                  }}
                  onClick={() => {
                    setShowDropdown(false);
                    setShowPDFLink(true);
                  }}
                >
                  Download
                </button>
              )} */}
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "#d32f2f",
                  fontSize: 18,
                  fontWeight: 500,
                  padding: "12px 24px",
                  textAlign: "left",
                  cursor: "pointer",
                  width: "100%",
                }}
                onClick={() => {
                  setShowDropdown(false);
                  navigate("/", { state: { from: location.pathname }});
                }}
              >
                Exit
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 48 }}>
            {/* Informational Button */}
            {/* {showInformational && (
              <button
                style={{
                  background: "#222",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "10px 32px",
                  fontSize: 18,
                  fontWeight: 500,
                  cursor: "pointer",
                  marginRight: 8,
                }}
                onClick={() => {
                  // Check if we're on main results page and should scroll instead of navigate
                  if (location.pathname === "/quiz/results/main") {
                    // @ts-ignore
                    if (window.scrollToImportanceSection) {
                      // @ts-ignore
                      window.scrollToImportanceSection();
                    }
                  } else {
                    navigate(buttonRoute, { state: { ...safeResultsData, from: location.pathname }});
                  }
                }}
              >
                {buttonText}
              </button>
            )} */}
            {showDownload && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginRight: 0,
                }}
              >
                {!showPDFLink ? (
                  <button
                    style={{
                      background: "#f8f8f8",
                      color: "#888",
                      border: "1px solid #eee",
                      borderRadius: 12,
                      padding: "10px 32px",
                      fontSize: 18,
                      fontWeight: 500,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: 0.7,
                      textDecoration: "none",
                      marginRight: 0,
                    }}
                    onClick={() => setShowPDFLink(true)}
                  >
                    <SvgDownload color={colors.gray[600]} width={20} height={20} />
                    Download my PDF
                  </button>
                ) : (
                  <PDFDownloadLink
                  document={
                    <PersonalityTestPDF
                      answers={mainAnswers ?? []}
                      categoryScores={mainCategoryScores ?? {}}
                      dominantCategory={mainDominantCategory ?? ""}
                      personalInfo={parsedPersonalInfo}
                    />
                  }
                  fileName={`financial-personality-profile-${new Date().toISOString().split('T')[0]}.pdf`}
                    style={{
                      background: "#f8f8f8",
                      color: "#888",
                      border: "1px solid #eee",
                      borderRadius: 12,
                      padding: "10px 32px",
                      fontSize: 18,
                      fontWeight: 500,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: 0.7,
                      textDecoration: "none",
                      marginRight: 0,
                    }}
                  >
                    {({ loading, url }) => {
                      if (url && showPDFLink) {
                        setTimeout(() => {
                          if (anchorRef.current) {
                            anchorRef.current.click();
                            setShowPDFLink(false);
                          }
                        }, 0);
                      }
                      return (
                        <>
                          <CustomText style={{
                            color: colors.gray[600],
                            fontSize: 18,
                            fontWeight: '500',
                            fontFamily: "Nunito",
                          }}>
                            {loading ? 'Generating PDF...' : 'Download results'}
                          </CustomText>
                          <SvgDownload color={colors.gray[600]} width={20} height={20} />
                          {url && showPDFLink && (
                            <a
                              href={url || undefined}
                              ref={anchorRef}
                              style={{ display: "none" }}
                              download={`financial-personality-profile-${new Date().toISOString().split('T')[0]}.pdf`}
                            >
                              Download PDF
                            </a>
                          )}
                        </>
                      );
                    }}
                  </PDFDownloadLink>
                )}
              </div>
            )}
            <button
              onClick={() => navigate("/", { state: { from: location.pathname }})}
              style={{
                width: "25px",
                height: "25px",
                background: "none",
                border: "none",
                fontSize: 32,
                fontWeight: 700,
                color: "#222",
                cursor: "pointer",
                lineHeight: 1,
                padding: 0,
                borderRadius: "100%",
              }}
              aria-label="Close"
            >
              <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
      </div>
      {/* Gear background image, repeated */}
      <div
        style={{
          position: "static",
          width: "100%",
          minHeight: "calc(100vh - 100px)",
          overflow: "hidden",
        }}
      >
        {/* Background */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            backgroundImage: "url('/quizbg-v2.png')",
            backgroundRepeat: "repeat",
            backgroundSize: "1920px 1080px",
            opacity: 1,
            pointerEvents: "none",
          }}
        />
        {/* Main Content */}
        <div style={{ position: "relative", zIndex: 1, marginTop: 100 }}>
          <Outlet />
        </div>
      </div>
      {/* PDFDownloadLink is always rendered, but hidden. Used for programmatic download on both desktop and mobile. */}
      <div style={{ display: 'none' }}>
                        <PDFDownloadLink
                  document={
                    <PersonalityTestPDF
                      answers={mainAnswers || []}
                      categoryScores={mainCategoryScores || {}}
                      dominantCategory={mainDominantCategory || ""}
                      personalInfo={parsedPersonalInfo}
                    />
                  }
          fileName={`financial-personality-profile-${new Date().toISOString().split('T')[0]}.pdf`}
        >
          {({ loading, url }) => {
            if (url && showPDFLink) {
              setTimeout(() => {
                if (anchorRef.current) {
                  anchorRef.current.click();
                  setShowPDFLink(false);
                }
              }, 0);
            }
            return (
              <a
                href={url || undefined}
                ref={anchorRef}
                style={{ display: "none" }}
                download={`financial-personality-profile-${new Date().toISOString().split('T')[0]}.pdf`}
              >
                Download PDF
              </a>
            );
          }}
        </PDFDownloadLink>
      </div>
    </>
  );
}